'use client';

import React, { useCallback, useMemo, useRef, useState, type ComponentType } from 'react';
import {
  InquiryFlowLoader,
  InquiryFlowSkeleton,
  Button,
  type TerminalProps,
} from '@miso/design-system';
import type { FlowDocument } from '@miso/design-system';
import { myVisitCounselApi } from '@/entities/planner/model/my-planner.api';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import {
  AddRoomNameSheet,
  AddRoomTypeSheet,
  type AddRoomTypeOption,
} from '@/features/visit-counsel/room-add-sheets';
import {
  mapHouseStructureFlowResult,
  transformRoomsToFlowRooms,
  type FlowRoom,
  type TransformRoom,
} from '../model/mapHouseStructureFlowResult';

// ── Flow 로더 ──────────────────────────────────────────────────────────────────

async function fetchFlow(flowId: string): Promise<FlowDocument> {
  const res = await fetch(`/api/v1/flows/${encodeURIComponent(flowId)}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? `Flow '${flowId}' 로드 실패`);
  }
  return res.json() as Promise<FlowDocument>;
}

const fetchHouseStructureFlow = (): Promise<FlowDocument> =>
  fetchFlow('house-structure-record-flow');

// ── 방 타입 옵션 ───────────────────────────────────────────────────────────────
//
// NOTE: 본 어댑터는 한국어 라벨을 그대로 value 로 사용하는 구버전 도메인이다
// (정식 RoomTypeValue 열거형은 RoomEditFlowAdapter 가 사용).
// `mapHouseStructureFlowResult` 가 한국어 라벨 → API 열거형 매핑을 담당한다.
// 'other' 는 OTHER_SPACE 로 매핑되며 사용자가 커스텀 이름을 입력해야 한다.

type RoomTypeOptionValue = '현관' | '방' | '거실' | '화장실' | '세대창고' | '베란다' | 'other';

const ROOM_TYPE_OPTIONS: ReadonlyArray<AddRoomTypeOption<RoomTypeOptionValue>> = [
  { value: '현관', label: '현관' },
  { value: '방', label: '방' },
  { value: '거실', label: '거실' },
  { value: '화장실', label: '화장실' },
  { value: '세대창고', label: '세대창고' },
  { value: '베란다', label: '베란다' },
  { value: 'other', label: '그 외' },
];

// ── 인라인 스타일 ─────────────────────────────────────────────────────────────

const s = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    background: '#ffffff',
    fontFamily: 'var(--font-sans, sans-serif)',
  },
  header: { padding: '56px 24px 20px' },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#111827',
    letterSpacing: '-0.025em',
    lineHeight: 1.35,
    margin: 0,
  },
  scroll: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '0 24px 16px',
    minHeight: 0,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '13px 0',
    borderBottom: '1px solid #f3f4f6',
  },
  rowLabel: {
    fontSize: 16,
    color: '#111827',
    fontWeight: 500,
    letterSpacing: '-0.02em',
    flexShrink: 0,
  },
  tag: {
    fontSize: 11,
    fontWeight: 600,
    color: '#2563EB',
    background: '#EFF6FF',
    borderRadius: 4,
    padding: '2px 6px',
    whiteSpace: 'nowrap' as const,
    letterSpacing: '-0.01em',
  },
  tagEditable: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    fontSize: 11,
    fontWeight: 600,
    color: '#2563EB',
    background: '#EFF6FF',
    borderRadius: 4,
    padding: '2px 4px 2px 6px',
    whiteSpace: 'nowrap' as const,
    letterSpacing: '-0.01em',
  },
  tagRemoveBtn: {
    background: 'none',
    border: 'none',
    padding: '0 1px',
    cursor: 'pointer',
    color: '#93c5fd',
    fontSize: 10,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
  },
  removeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#9ca3af',
    fontSize: 18,
    lineHeight: 1,
    padding: '2px 4px',
    flexShrink: 0,
  },
  addRoomBtn: {
    alignSelf: 'flex-start' as const,
    height: 34,
    padding: '0 14px',
    borderRadius: 8,
    background: 'transparent',
    border: '1px dashed #d1d5db',
    color: '#6b7280',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    letterSpacing: '-0.02em',
    marginTop: 12,
  },
  footer: {
    padding: '16px 20px 32px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  },
};

// ── RoomRow ───────────────────────────────────────────────────────────────────

interface RoomRowProps {
  label: string;
  tags: string[];
  onRemove?: () => void;
  onRemoveTag?: (tag: string) => void;
}

function RoomRow({ label, tags, onRemove, onRemoveTag }: RoomRowProps) {
  return (
    <div style={s.row}>
      <span style={s.rowLabel}>{label}</span>
      {tags.map((tag) => (
        <span key={tag} style={onRemoveTag ? s.tagEditable : s.tag}>
          {tag}
          {onRemoveTag && (
            <button
              style={s.tagRemoveBtn}
              onClick={() => onRemoveTag(tag)}
              aria-label={`${tag} 태그 삭제`}
            >
              ×
            </button>
          )}
        </span>
      ))}
      <span style={{ flex: 1 }} />
      {onRemove && (
        <button style={s.removeBtn} onClick={onRemove} aria-label={`${label} 삭제`}>
          ×
        </button>
      )}
    </div>
  );
}

// ── DoneScreen factory ────────────────────────────────────────────────────────

interface DoneScreenOptions {
  onConfirm: (rooms: FlowRoom[]) => Promise<void>;
  getContainer: () => HTMLElement | null;
}

function makeDoneScreen({
  onConfirm,
  getContainer,
}: DoneScreenOptions): ComponentType<TerminalProps> {
  return function HouseStructureDoneScreen({ values, onUpdateAccumulated }: TerminalProps) {
    // transform step이 { rooms: result } (plain object)를 반환하고 outputKey='rooms'이므로
    // accumulated.rooms = { rooms: TransformRoom[] } 형태.
    const roomsContainer = (values as Record<string, unknown>).rooms as
      | { rooms?: unknown[] }
      | undefined;
    const rawRooms = Array.isArray(roomsContainer?.rooms)
      ? (roomsContainer!.rooms as TransformRoom[])
      : [];
    const transformRooms = transformRoomsToFlowRooms(rawRooms);

    const [confirmedRooms, setConfirmedRooms] = useState<FlowRoom[] | null>(null);
    const [mode, setMode] = useState<'view' | 'edit'>('view');
    const [editRooms, setEditRooms] = useState<FlowRoom[]>([]);
    const [typeSheetOpen, setTypeSheetOpen] = useState(false);
    const [selectedType, setSelectedType] = useState<RoomTypeOptionValue | null>(null);
    const [nameSheetOpen, setNameSheetOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const displayRooms = confirmedRooms ?? transformRooms;

    /*
     * BottomSheet 가 떠 있을 때는 underlying footer CTA("확인" 또는 "다음") 를
     * 숨긴다 — 사용자 정책: "모든 바텀시트는 그 이전의 CTA 를 숨겨야 한다".
     * sheet 의 자체 backdrop(rgba(0,0,0,0.15)) 만으로는 시각적 충돌이 남아서
     * 시트가 열린 동안에는 페이지 하단 CTA 를 완전히 unmount 한다.
     */
    const anySheetOpen = typeSheetOpen || nameSheetOpen;

    const enterEdit = () => {
      setEditRooms(displayRooms.map((r) => ({ ...r, tags: [...r.tags] })));
      setMode('edit');
    };

    const confirmEdit = () => {
      const saved = editRooms.map((r) => ({ ...r, tags: [...r.tags] }));
      setConfirmedRooms(saved);
      setMode('view');
      onUpdateAccumulated({ _edited_rooms: saved });
    };

    const removeRoom = (id: string) => setEditRooms((prev) => prev.filter((r) => r.id !== id));
    const removeTag = (roomId: string, tag: string) =>
      setEditRooms((prev) =>
        prev.map((r) => (r.id === roomId ? { ...r, tags: r.tags.filter((t) => t !== tag) } : r))
      );

    const openTypeSheet = () => {
      setSelectedType(null);
      setTypeSheetOpen(true);
    };

    const confirmType = () => {
      if (!selectedType) return;
      if (selectedType === 'other') {
        setTypeSheetOpen(false);
        setNameInput('');
        setNameSheetOpen(true);
      } else {
        setEditRooms((prev) => [
          ...prev,
          { id: `add_${Date.now()}`, label: selectedType, tags: [] },
        ]);
        setTypeSheetOpen(false);
        setSelectedType(null);
      }
    };

    const confirmName = () => {
      const name = nameInput.trim();
      if (!name) return;
      setEditRooms((prev) => [...prev, { id: `add_${Date.now()}_c`, label: name, tags: [] }]);
      setNameSheetOpen(false);
      setNameInput('');
    };

    const handleNext = async () => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        await onConfirm(displayRooms);
        // 성공 경로: 상위가 stage 전환으로 본 컴포넌트를 unmount 시킨다.
        // 여기서 isSubmitting 을 false 로 되돌리면 unmount 전에 "다음" 으로 잠깐 깜빡임이
        // 발생하므로 의도적으로 reset 하지 않는다. 만약 stage 전환에 실패해 컴포넌트가
        // 남아 있다면 onAfterDone 이 throw 하여 아래 catch 가 reset 한다.
      } catch {
        setIsSubmitting(false);
      }
    };

    const container = getContainer();

    if (mode === 'edit') {
      return (
        <div style={s.container}>
          <div style={s.header}>
            <p style={s.title}>
              방 구조를
              <br />
              수정해주세요
            </p>
          </div>
          <div style={s.scroll}>
            {editRooms.map((room) => (
              <RoomRow
                key={room.id}
                label={room.label}
                tags={room.tags}
                onRemove={() => removeRoom(room.id)}
                onRemoveTag={(tag) => removeTag(room.id, tag)}
              />
            ))}
            <button style={s.addRoomBtn} onClick={openTypeSheet}>
              + 방 추가하기
            </button>
          </div>
          {!anySheetOpen && (
            <div style={s.footer}>
              <Button variant="primary" size="l" width="fixed" onPress={confirmEdit}>
                확인
              </Button>
            </div>
          )}

          <AddRoomTypeSheet<RoomTypeOptionValue>
            open={typeSheetOpen}
            options={ROOM_TYPE_OPTIONS}
            selected={selectedType}
            onSelect={setSelectedType}
            onConfirm={confirmType}
            onClose={() => setTypeSheetOpen(false)}
            container={container}
          />

          <AddRoomNameSheet
            open={nameSheetOpen}
            value={nameInput}
            onChange={setNameInput}
            onConfirm={confirmName}
            onClose={() => setNameSheetOpen(false)}
            container={container}
          />
        </div>
      );
    }

    return (
      <div style={s.container}>
        <div style={s.header}>
          <p style={s.title}>
            방 구조가
            <br />
            완성되었어요!
          </p>
        </div>
        <div style={s.scroll}>
          {displayRooms.map((room) => (
            <RoomRow key={room.id} label={room.label} tags={room.tags} />
          ))}
        </div>
        <div style={s.footer}>
          <Button
            variant="primary"
            size="l"
            width="fixed"
            onPress={handleNext}
            disabled={isSubmitting}
          >
            {isSubmitting ? '저장 중...' : '다음'}
          </Button>
          <Button variant="text" size="s" width="fixed" onPress={enterEdit}>
            수정이 필요해요
          </Button>
        </div>
      </div>
    );
  };
}

// ── Adapter ───────────────────────────────────────────────────────────────────

export interface HouseStructureFlowAdapterProps {
  visitCounselId: string;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'neutral') => void;
  /** POST API 응답으로 SWR 캐시를 즉시 갱신하는 함수 (stage 즉시 전환을 위해 필수) */
  mutateWithCounsel: (counsel: VisitCounselView) => void;
  /**
   * POST 성공 후 호출. Promise 를 반환하면 어댑터가 그 완료까지 await 하므로
   * 그 동안 CTA "다음" 버튼은 "저장 중..." 로 표시된 채 유지된다.
   * (e.g. 백엔드 eventual consistency 폴링이 끝날 때까지 사용자가 기다리도록 함)
   */
  onAfterDone: (counsel: VisitCounselView) => void | Promise<void>;
  onCancel: () => void;
  /** BottomSheet 포탈용 컨테이너 (없으면 body 기준) */
  container?: HTMLElement | null;
}

/**
 * house-structure-record-flow를 production API와 연결하는 어댑터.
 *
 * 흐름:
 *   1. house-structure-record-flow 표시 (집 구조 질문)
 *   2. transform step → accumulated.rooms 생성
 *   3. terminal "house-structure-done" 표시 (방 목록 확인/편집)
 *   4. "다음" CTA → POST /sketching-session/start → onAfterDone
 */
export function HouseStructureFlowAdapter({
  visitCounselId,
  showToast,
  onAfterDone,
  onCancel,
  container,
}: HouseStructureFlowAdapterProps) {
  // terminal의 BottomSheet가 포털할 컨테이너.
  // - 외부에서 container prop을 받으면 그것을 사용.
  // - 없으면 래퍼 div를 사용 (useRef로 관리 — useState는 InquiryFlow 렌더 중 상위 setState를 유발함).
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLElement | null>(container ?? null);
  containerRef.current = container ?? wrapperRef.current;

  const terminals = useMemo(() => {
    const onConfirm = async (rooms: FlowRoom[]) => {
      const accumulated: Record<string, unknown> = { rooms };
      const roomSketchings = mapHouseStructureFlowResult(accumulated);

      // POST 자체 실패만 여기서 토스트를 띄운다.
      // onAfterDone(폴링) 의 timeout/실패는 내부에서 사용자 메시지를 처리하므로
      // 여기서 중복 토스트하지 않고 단순히 re-throw 해 상위(handleNext)가 버튼을 reset.
      let updatedCounsel: VisitCounselView;
      try {
        updatedCounsel = await myVisitCounselApi.startHouseSketchSession(visitCounselId, {
          roomSketchings,
        });
      } catch (err) {
        showToast(err instanceof Error ? err.message : '방 구조 기록에 실패했습니다', 'error');
        throw err;
      }

      // SWR 즉시 갱신은 의도적으로 하지 않는다 — onAfterDone 이 GET 폴링으로 서버 반영을
      // 확인한 뒤에만 캐시를 갱신해야 stage 가 안전하게 전환된다.
      // 폴링 동안 await 가 pending → CTA "다음" 은 "저장 중..." 유지.
      await onAfterDone(updatedCounsel);
      showToast('방 구조를 반영했습니다', 'success');
    };

    return {
      'house-structure-done': makeDoneScreen({
        onConfirm,
        getContainer: () => containerRef.current,
      }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitCounselId, showToast, onAfterDone, onCancel]);

  const handleError = useCallback(
    (err: Error) => {
      showToast(err.message ?? '플로우 오류가 발생했습니다', 'error');
      onCancel();
    },
    [showToast, onCancel]
  );

  return (
    <div ref={wrapperRef} style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      <InquiryFlowLoader
        fetcher={fetchHouseStructureFlow}
        registries={{ terminals }}
        onComplete={() => {
          // terminal이 자체 CTA(onConfirm)로 API 호출·이동을 처리한다.
        }}
        onCancel={onCancel}
        onError={handleError}
        skeleton={<InquiryFlowSkeleton />}
      />
    </div>
  );
}
